import React from "react";
import { NavLink } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { HOME_PAGE_URL, TERMS_OF_SERVICE_PAGE_URL } from "../../utilities/constants/PagesEndpoints";

const PrivacyPolicyPage = () => {
  const lastUpdated = "February 15, 2026";

  return (
    <div className="bg-slate-50 dark:bg-slate-950 transition-colors duration-300 min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-white" />
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              Privacy Policy
            </h3>
          </div>
          <p className="text-slate-300 text-lg">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <NavLink
            to={HOME_PAGE_URL}
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </NavLink>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                1. Introduction
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Le Prestige Hall ("we," "our," or "us") is committed to protecting your privacy.
                This Privacy Policy explains how we collect, use, disclose, and safeguard your
                information when you visit our website or use our services. Please read this
                policy carefully. By using our services, you consent to the practices described
                in this Privacy Policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                2. Information We Collect
              </h2>

              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200 mb-3 mt-6">
                2.1 Personal Information
              </h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide when using our
                services, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Name, email address, and phone number</li>
                <li>Mailing address and billing information</li>
                <li>Date of birth and gender (optional)</li>
                <li>Event details and preferences</li>
                <li>Payment information (processed securely through third-party providers)</li>
                <li>Account login credentials</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200 mb-3 mt-6">
                2.2 Automatically Collected Information
              </h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>IP address and browser type</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Processing and managing your reservations and appointments</li>
                <li>Communicating with you about your events and our services</li>
                <li>Sending promotional emails and newsletters (with your consent)</li>
                <li>Improving our website and services</li>
                <li>Processing payments and preventing fraud</li>
                <li>Complying with legal obligations</li>
                <li>Responding to your inquiries and providing customer support</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                We do not sell your personal information. We may share your information in the
                following circumstances:
              </p>
              <div className="space-y-4 text-slate-600 dark:text-slate-300 leading-relaxed">
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">Service Providers:</strong> We
                  may share information with third-party vendors who perform services on our behalf,
                  such as payment processing, email delivery, and data analytics.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">Legal Requirements:</strong> We
                  may disclose information if required by law, court order, or government regulation.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">Business Transfers:</strong> In
                  the event of a merger, acquisition, or sale of assets, your information may be
                  transferred to the acquiring entity.
                </p>
                <p>
                  <strong className="text-slate-800 dark:text-slate-100">With Your Consent:</strong> We
                  may share information with third parties when you have given us explicit consent.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                5. Cookies and Tracking Technologies
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our
                website. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our website</li>
                <li>Improve our services and user experience</li>
                <li>Provide personalized content</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling cookies
                may limit your ability to use certain features of our website.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                6. Data Security
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect
                your personal information against unauthorized access, alteration, disclosure, or
                destruction. These measures include encryption, secure servers, and access controls.
                However, no method of transmission over the Internet is 100% secure, and we cannot
                guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                7. Data Retention
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes
                outlined in this Privacy Policy, unless a longer retention period is required by law.
                When your information is no longer needed, we will securely delete or anonymize it.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                8. Your Rights
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal
                information, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-slate-300 space-y-2 ml-4">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate information</li>
                <li>The right to delete your personal information</li>
                <li>The right to opt-out of marketing communications</li>
                <li>The right to data portability</li>
                <li>The right to withdraw consent</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                9. Third-Party Links
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for
                the privacy practices or content of these external sites. We encourage you to review
                the privacy policies of any third-party websites you visit.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly
                collect personal information from children. If we become aware that we have collected
                personal information from a child, we will take steps to delete that information.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                We may update this Privacy Policy from time to time. Any changes will be posted on
                this page with an updated "Last updated" date. We encourage you to review this
                Privacy Policy periodically for any changes.
              </p>
            </section>

            {/* Terms of Service Link */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                12. Terms of Service
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Please also review our{" "}
                <NavLink
                  to={TERMS_OF_SERVICE_PAGE_URL}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Terms of Service
                </NavLink>
                , which govern your use of our website and services.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                13. Contact Us
              </h2>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-6">
                <p className="text-slate-700 dark:text-slate-200 font-semibold">Le Prestige Hall</p>
                <p className="text-slate-600 dark:text-slate-300">Columbus, Ohio</p>
                <p className="text-slate-600 dark:text-slate-300">
                  Email:{" "}
                  <a
                    href="mailto:privacy@leprestigehall.net"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    privacy@leprestigehall.net
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
